import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Ban,
  Search,
  Download,
  Eye,
  Unlock,
  Clock,
  User,
  AlertTriangle,
  MoreHorizontal,
  RefreshCcw,
  History,
  MessageSquare,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Pagination,
} from '@/components/ui/Table';
import { Modal, ConfirmDialog, DropdownMenu } from '@/components/ui/Modal';
import {
  formatDate,
  formatDateTime,
  formatRelativeTime,
  formatPhone,
  getInitials,
  cn,
} from '@/lib/utils';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/lib/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Interface for banned user
interface BannedUser {
  _id: string;
  name: string;
  phone: string;
  email: string;
  avatar: string | null;
  bannedAt: string;
  bannedBy: string;
  bannedByName: string;
  banReason: string;
  banType: 'permanent' | 'temporary';
  banExpiry?: string;
  isKycVerified: boolean;
  stats: {
    gamesPlayed: number;
    totalDeposits: number;
    totalWithdrawals: number;
    walletBalance: number;
  };
  previousBans: number;
  appealStatus: 'pending' | 'approved' | 'rejected' | null;
  lastLogin: string;
  registeredAt: string;
}

export default function BannedUsers() {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [banTypeFilter, setBanTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch banned users from API
  const { data: apiResponse, isLoading: loading, refetch: refetchUsers } = useQuery({
    queryKey: ['bannedUsers', currentPage, searchQuery],
    queryFn: async () => {
      try {
        const params: any = {
          page: currentPage,
          limit: 50,
          status: 'banned', // Filter for banned users only
        };
        if (searchQuery) params.search = searchQuery;

        const response = await usersApi.getUsers(params);
        return response.data.data;
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to fetch banned users');
        throw error;
      }
    },
    staleTime: 30000,
  });

  // Transform API data to match the expected format
  const users: BannedUser[] = useMemo(() => {
    if (apiResponse?.users && Array.isArray(apiResponse.users)) {
      return apiResponse.users
        .filter((u: any) => u.isBanned === true) // Ensure only banned users
        .map((u: any) => ({
          _id: u._id || u.id,
          name: u.name || 'Unknown User',
          phone: u.phone || '',
          email: u.email || '',
          avatar: u.avatar || null,
          bannedAt: u.bannedAt || u.updatedAt || new Date().toISOString(),
          bannedBy: '',
          bannedByName: 'Admin',
          banReason: u.banReason || 'No reason provided',
          banType: 'permanent' as const, // Default to permanent, adjust if you have banExpiry
          banExpiry: undefined,
          isKycVerified: u.isKycVerified || false,
          stats: {
            gamesPlayed: u.stats?.gamesPlayed || u.totalGamesPlayed || 0,
            totalDeposits: u.stats?.totalDeposits || 0,
            totalWithdrawals: u.stats?.totalWithdrawals || 0,
            walletBalance: u.walletBalance || 0,
          },
          previousBans: 0,
          appealStatus: null,
          lastLogin: u.lastLogin || '',
          registeredAt: u.createdAt || new Date().toISOString(),
        }));
    }
    return [];
  }, [apiResponse]);

  // Modals
  const [unbanModalOpen, setUnbanModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BannedUser | null>(null);
  const [userDetailModalOpen, setUserDetailModalOpen] = useState(false);

  const itemsPerPage = 10;

  // Filter users
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone.includes(searchQuery) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = banTypeFilter === 'all' || user.banType === banTypeFilter;
    return matchesSearch && matchesType;
  });

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Handlers
  const handleUnban = (user: BannedUser) => {
    setSelectedUser(user);
    setUnbanModalOpen(true);
  };

  const confirmUnban = async () => {
    if (selectedUser) {
      try {
        await usersApi.unbanUser(selectedUser._id);
        toast.success(`${selectedUser.name} has been unbanned`);
        refetchUsers(); // Refresh the list after unbanning
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to unban user');
      }
    }
    setUnbanModalOpen(false);
    setSelectedUser(null);
  };

  const handleViewDetails = (user: BannedUser) => {
    setSelectedUser(user);
    setUserDetailModalOpen(true);
  };

  const handleRefresh = async () => {
    await refetchUsers();
    toast.success('Data refreshed');
  };

  const getAppealStatusBadge = (status: string | null) => {
    if (!status) return null;
    const configs: Record<string, { color: string; label: string }> = {
      pending: { color: 'bg-amber-500/10 text-amber-400', label: 'Appeal Pending' },
      approved: { color: 'bg-emerald-500/10 text-emerald-400', label: 'Appeal Approved' },
      rejected: { color: 'bg-red-500/10 text-red-400', label: 'Appeal Rejected' },
    };
    const config = configs[status];
    return (
      <span className={cn('px-2 py-0.5 rounded-md text-xs font-medium', config.color)}>
        {config.label}
      </span>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Ban className="w-7 h-7 text-red-400" />
            Banned Users
          </h1>
          <p className="text-slate-400 mt-1">Manage and review banned user accounts</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCcw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} />
            Refresh
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <Ban className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{users.length}</p>
              <p className="text-xs text-slate-400">Total Banned</p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {users.filter((u) => u.banType === 'temporary').length}
              </p>
              <p className="text-xs text-slate-400">Temporary Bans</p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {users.filter((u) => u.banType === 'permanent').length}
              </p>
              <p className="text-xs text-slate-400">Permanent Bans</p>
            </div>
          </div>
        </Card>
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {users.filter((u) => u.appealStatus === 'pending').length}
              </p>
              <p className="text-xs text-slate-400">Pending Appeals</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div variants={itemVariants}>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by name, phone, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                />
              </div>
              <select
                value={banTypeFilter}
                onChange={(e) => setBanTypeFilter(e.target.value)}
                className="px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-white focus:outline-none focus:ring-2 focus:ring-violet-500/50"
              >
                <option value="all">All Ban Types</option>
                <option value="permanent">Permanent</option>
                <option value="temporary">Temporary</option>
              </select>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Users Table */}
      <motion.div variants={itemVariants}>
        <Card variant="glass">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Ban Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Banned By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Appeal</TableHead>
                  <TableHead className="w-12">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center text-white font-medium text-sm">
                          {getInitials(user.name)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{user.name}</p>
                          <p className="text-xs text-slate-500">{formatPhone(user.phone)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={cn(
                        'px-2.5 py-1 rounded-lg text-xs font-medium',
                        user.banType === 'permanent'
                          ? 'bg-red-500/10 text-red-400'
                          : 'bg-amber-500/10 text-amber-400'
                      )}>
                        {user.banType === 'permanent' ? 'Permanent' : 'Temporary'}
                      </span>
                      {user.banExpiry && (
                        <p className="text-xs text-slate-500 mt-1">
                          Expires: {formatDate(user.banExpiry)}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-300 max-w-xs truncate">
                        {user.banReason}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-300">{user.bannedByName}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-slate-300">{formatDate(user.bannedAt)}</p>
                      <p className="text-xs text-slate-500">{formatRelativeTime(user.bannedAt)}</p>
                    </TableCell>
                    <TableCell>
                      {getAppealStatusBadge(user.appealStatus)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu
                        trigger={
                          <button className="p-2 rounded-lg hover:bg-slate-800 transition-colors">
                            <MoreHorizontal className="w-4 h-4 text-slate-400" />
                          </button>
                        }
                        items={[
                          {
                            label: 'View Details',
                            icon: Eye,
                            onClick: () => handleViewDetails(user),
                          },
                          {
                            label: 'View Profile',
                            icon: User,
                            onClick: () => navigate(`/users/${user._id}`),
                          },
                          {
                            label: 'Ban History',
                            icon: History,
                            onClick: () => toast.success('Opening ban history...'),
                          },
                          { type: 'separator' },
                          {
                            label: 'Unban User',
                            icon: Unlock,
                            onClick: () => handleUnban(user),
                            variant: 'success',
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredUsers.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredUsers.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Unban Confirmation Modal */}
      <ConfirmDialog
        isOpen={unbanModalOpen}
        onClose={() => {
          setUnbanModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={confirmUnban}
        title="Unban User"
        description={`Are you sure you want to unban ${selectedUser?.name}? They will be able to access the platform again.`}
        confirmText="Unban User"
        variant="warning"
      />

      {/* User Detail Modal */}
      <Modal
        isOpen={userDetailModalOpen}
        onClose={() => {
          setUserDetailModalOpen(false);
          setSelectedUser(null);
        }}
        title="Ban Details"
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            {/* User Info */}
            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/50">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center text-white font-bold text-lg">
                {getInitials(selectedUser.name)}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">{selectedUser.name}</h3>
                <p className="text-sm text-slate-400">{formatPhone(selectedUser.phone)}</p>
                <p className="text-sm text-slate-400">{selectedUser.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  {selectedUser.isKycVerified ? (
                    <span className="px-2 py-0.5 rounded-md text-xs bg-emerald-500/10 text-emerald-400">
                      KYC Verified
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-md text-xs bg-slate-500/10 text-slate-400">
                      KYC Not Verified
                    </span>
                  )}
                  <span className="px-2 py-0.5 rounded-md text-xs bg-slate-500/10 text-slate-400">
                    {selectedUser.previousBans} Previous Bans
                  </span>
                </div>
              </div>
            </div>

            {/* Ban Info */}
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-red-500/5 border border-red-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Ban Type</span>
                  <span className={cn(
                    'px-2.5 py-1 rounded-lg text-xs font-medium',
                    selectedUser.banType === 'permanent'
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-amber-500/10 text-amber-400'
                  )}>
                    {selectedUser.banType === 'permanent' ? 'Permanent' : 'Temporary'}
                  </span>
                </div>
                {selectedUser.banExpiry && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-400">Expiry Date</span>
                    <span className="text-sm text-white">{formatDateTime(selectedUser.banExpiry)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Banned At</span>
                  <span className="text-sm text-white">{formatDateTime(selectedUser.bannedAt)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">Banned By</span>
                  <span className="text-sm text-white">{selectedUser.bannedByName}</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-800/50">
                <p className="text-sm text-slate-400 mb-2">Ban Reason</p>
                <p className="text-white">{selectedUser.banReason}</p>
              </div>

              {/* User Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                  <p className="text-lg font-bold text-white">{selectedUser.stats.gamesPlayed}</p>
                  <p className="text-xs text-slate-500">Games Played</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                  <p className="text-lg font-bold text-white">₹{selectedUser.stats.totalDeposits.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total Deposits</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                  <p className="text-lg font-bold text-white">₹{selectedUser.stats.totalWithdrawals.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Total Withdrawals</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-800/50 text-center">
                  <p className="text-lg font-bold text-white">₹{selectedUser.stats.walletBalance.toLocaleString()}</p>
                  <p className="text-xs text-slate-500">Wallet Balance</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-slate-800">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/users/${selectedUser._id}`)}
              >
                <User className="w-4 h-4 mr-2" />
                View Full Profile
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={() => {
                  setUserDetailModalOpen(false);
                  handleUnban(selectedUser);
                }}
              >
                <Unlock className="w-4 h-4 mr-2" />
                Unban User
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
